import React, { useState, useEffect } from 'react';
import { Music, Calendar, Clock, Target } from 'lucide-react';
import { practiceSessionService } from '../../services/practiceSessionService';
import SessionHistory from './components/SessionHistory';
import PerformanceBreakdown from './components/PerformanceBreakdown';
import InsightsPanel from './components/InsightsPanel';

export default function PracticeSessionAnalyticsDashboard() {
  const [sessions, setSessions] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [streakStats, setStreakStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('week');

  useEffect(() => {
    loadDashboardData();
  }, [dateRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const filters = getDateFilters(dateRange);

      const [sessionsData, analyticsData, streakData] = await Promise.all([
        practiceSessionService?.getSessions(filters),
        practiceSessionService?.getAnalytics(filters),
        practiceSessionService?.getStreakStats()
      ]);

      setSessions(sessionsData);
      setAnalytics(analyticsData);
      setStreakStats(streakData);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getDateFilters = (range) => {
    const now = new Date();
    const filters = {};

    switch (range) {
      case 'week':
        filters.startDate = new Date(now.setDate(now.getDate() - 7))?.toISOString();
        break;
      case 'month':
        filters.startDate = new Date(now.setMonth(now.getMonth() - 1))?.toISOString();
        break;
      case 'quarter':
        filters.startDate = new Date(now.setMonth(now.getMonth() - 3))?.toISOString();
        break;
      default:
        break;
    }

    return filters;
  };

  const completedSessions = sessions?.filter(s => s?.status === 'completed');
  const avgCompletionRate = completedSessions?.length > 0
    ? completedSessions?.reduce((sum, s) => sum + s?.completionRate, 0) / completedSessions?.length
    : 0;
  const avgScore = completedSessions?.length > 0
    ? completedSessions?.reduce((sum, s) => sum + s?.overallScore, 0) / completedSessions?.length
    : 0;
  const totalPracticeTime = completedSessions?.reduce((sum, s) => sum + (s?.totalDurationSeconds || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                🎭 Practice Session Analytics
              </h1>
              <p className="text-white/80">
                Track your progress across Breath Coach, Pitch Coach, and Audition Song tools
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e?.target?.value)}
                className="px-4 py-2 bg-white/20 text-white rounded-xl border border-white/30 focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="quarter">Last 3 Months</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 backdrop-blur-md rounded-2xl border border-red-500/30 p-4">
            <p className="text-white">{error}</p>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-500/30 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-300" />
              </div>
              <h3 className="text-white/80 text-sm font-medium">Current Streak</h3>
            </div>
            <p className="text-3xl font-bold text-white">
              {streakStats?.currentStreak || 0} days
            </p>
            <p className="text-white/60 text-sm mt-1">
              Longest: {streakStats?.longestStreak || 0} days
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-500/30 rounded-lg">
                <Music className="w-6 h-6 text-blue-300" />
              </div>
              <h3 className="text-white/80 text-sm font-medium">Total Sessions</h3>
            </div>
            <p className="text-3xl font-bold text-white">{completedSessions?.length}</p>
            <p className="text-white/60 text-sm mt-1">
              {sessions?.filter(s => s?.status === 'in_progress')?.length} in progress
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-500/30 rounded-lg">
                <Target className="w-6 h-6 text-green-300" />
              </div>
              <h3 className="text-white/80 text-sm font-medium">Avg Completion</h3>
            </div>
            <p className="text-3xl font-bold text-white">
              {Math.round(avgCompletionRate)}%
            </p>
            <p className="text-white/60 text-sm mt-1">
              Score: {Math.round(avgScore)}%
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-yellow-500/30 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-300" />
              </div>
              <h3 className="text-white/80 text-sm font-medium">Practice Time</h3>
            </div>
            <p className="text-3xl font-bold text-white">
              {Math.round(totalPracticeTime / 60)} min
            </p>
            <p className="text-white/60 text-sm mt-1">
              {Math.round(totalPracticeTime / 3600)}h total
            </p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Session History - Takes 2 columns */}
          <div className="lg:col-span-2">
            <SessionHistory sessions={sessions} onRefresh={loadDashboardData} />
          </div>

          {/* Insights Panel - Takes 1 column */}
          <div>
            <InsightsPanel 
              analytics={analytics} 
              streakStats={streakStats}
              sessions={completedSessions}
            />
          </div>
        </div>

        {/* Performance Breakdown */}
        <PerformanceBreakdown analytics={analytics} sessions={completedSessions} />
      </div>
    </div>
  );
}